import { Injectable } from '@angular/core';

declare const Stripe: any;
declare const moment: any;

import { ApiService, LogService, AppContext, Plan, Coupon } from './../shared';
import { Customer } from './models/customer';

@Injectable()
export class BillingService {

  constructor(private api: ApiService, private logger: LogService, private app: AppContext) {
  }

  createToken(card: any): Promise<any> {
    return new Promise((resolve, reject) => {
      try {
        Stripe.card.createToken(card, (status: number, response: any) => {
          this.logger.log('billing.service', 'stripeResponse', response);
          if (status === 200) {
            resolve(response);
          } else {
            reject(response);
          }
        });
      } catch (ex) {
        reject(ex);
      }
    });
  }

  checkCoupon(coupon: string): Promise<any> {
    return this.api.getApiClient().then(client => this.callCheckCoupon(client, coupon));
  }

  private callCheckCoupon(apigClient, coupon): Promise<any> {
    this.logger.log('billing.service', 'callCheckCoupon', 'before promise');
    return new Promise((resolve, reject) => {
      let params = {
        coupon: coupon
      };
      let body = {};
      let additionalParams = {
        headers: { 'Content-Type': 'application/json' },
        queryParams: {
          coupon: coupon
        }
      };
      try {
        apigClient.billingCouponGet(params, '', additionalParams)
          .then((result) => {
            this.logger.log('billing.service', 'callCheckCoupon result', result);
            let model = new Coupon(result.data);
            resolve(model);
          })
          .catch((err) => {
            reject(err);
          });
      } catch (ex) {
        reject(ex);
      }
    });
  }

  getPlans(): Promise<Plan[]> {
    return this.api.getApiClient().then(client => this.callGetPlans(client));
  }

  private callGetPlans(apigClient): Promise<Plan[]> {
    this.logger.log('billing.service', 'callGetPlans', 'before promise');
    return new Promise((resolve, reject) => {
      let params = {};
      let body = {};
      let additionalParams = {
        headers: { 'Content-Type': 'application/json' },
        queryParams: {}
      };
      try {
        apigClient.billingPlansGet(params, '', additionalParams)
          .then((result) => {
            this.logger.log('billing.service', 'callGetPlans result', result);
            let planArray = new Array<Plan>();
            for (let current = 0; current < result.data.data.length; current++) {
              let plan = new Plan(result.data.data[current]);
              planArray.push(plan);
            }
            resolve(planArray);
          })
          .catch((err) => {
            reject(err);
          });
      } catch (ex) {
        reject(ex);
      }
    });
  }

  createCustomer(email, token): Promise<any> {
    return this.api.getApiClient().then(client => this.callCreateCustomer(client, email, token));
  }

  private callCreateCustomer(apigClient, email: string, token: string): Promise<any> {
    this.logger.log('billing.service', 'callCreateCustomer', 'before promise');
    return new Promise((resolve, reject) => {
      let params = {};
      let body = {
        email: email.toLowerCase(),
        source: token
      };
      let additionalParams = {
        headers: { 'Content-Type': 'application/json' },
        queryParams: {}
      };
      try {
        apigClient.billingCustomerPost(params, body, additionalParams)
          .then((result) => {
            this.logger.log('billing.service', 'callCreateCustomer result', result);
            this.clearCustomer();
            resolve(result.data);
          }).catch((err) => {
            reject(err);
          });
      } catch (ex) {
        reject(ex);
      }
    });
  }

  updateCustomer(token: string): Promise<any> {
    return this.api.getApiClient().then(client => this.callUpdateCustomer(client, token));
  }

  private callUpdateCustomer(apigClient, token: string): Promise<any> {
    this.logger.log('billing.service', 'callUpdateCustomer', 'before promise');
    return new Promise((resolve, reject) => {
      let params = {};
      let body = {
        source: token
      };
      let additionalParams = {
        headers: { 'Content-Type': 'application/json' },
        queryParams: {}
      };
      try {
        apigClient.billingCustomerPut(params, body, additionalParams)
          .then((result) => {
            this.logger.log('billing.service', 'callUpdateCustomer result', result);
            this.clearCustomer();
            resolve(result.data);
          }).catch((err) => {
            reject(err);
          });
      } catch (ex) {
        reject(ex);
      }
    });
  }

  subscribe(plan: string, coupon: string): Promise<any> {
    let body = {
      plan: plan,
      coupon: coupon
    };

    return this.api.getApiClient().then(client => this.callSubscribe(client, body));
  }

  private callSubscribe(apigClient: any, body: any): Promise<any> {
    this.logger.log('billing.service', 'callSubscribe', 'before promise');
    return new Promise((resolve, reject) => {
      let params = {};
      let additionalParams = {
        headers: { 'Content-Type': 'application/json' },
        queryParams: {}
      };
      try {
        apigClient.billingSubscribePost(params, body, additionalParams)
          .then((result) => {
            this.logger.log('billing.service', 'callSubscribe result', result);
            if (result.data.errorMessage !== undefined && result.data.errorMessage === '{}') {
              reject('you must provide your credit card details first');
            }
            this.clearCustomer();
            resolve(result);
          }).catch((err) => {
            reject(err);
          });
      } catch (ex) {
        reject(ex);
      }
    });
  }

  cancel(): Promise<any> {
    return this.api.getApiClient().then(client => this.callCancel(client));
  }

  private callCancel(apigClient: any): Promise<any> {
    this.logger.log('billing.service', 'callCancel', 'before promise');
    return new Promise((resolve, reject) => {
      let params = {
        '': ''
      };
      let body = {
        '': ''
      };
      let additionalParams = {
        headers: { 'Content-Type': 'application/json' },
        queryParams: {}
      };
      try {
        apigClient.billingSubscribeDelete(params, body, additionalParams)
          .then((result) => {
            this.logger.log('billing.service', 'callCancel result', result);
            this.clearCustomer();
            resolve(result);
          }).catch((err) => {
            reject(err);
          });
      } catch (ex) {
        reject(ex);
      }
    });
  }

  getInvoices(): Promise<any> {
    return this.api.getApiClient().then(client => this.callGetInvoices(client));
  }

  private callGetInvoices(apigClient: any): Promise<any> {
    this.logger.log('billing.service', 'callGetInvoices', 'before promise');
    return new Promise((resolve, reject) => {
      let params = {};
      let body = {};
      let additionalParams = {
        headers: { 'Content-Type': 'application/json' },
        queryParams: {}
      };
      try {
        apigClient.billingCustomerInvoicesGet(params, body, additionalParams)
          .then((result) => {
            this.logger.log('billing.service', 'callGetInvoices result', result);
            let formatted = this.formatInvoices(result.data);
            resolve(formatted);
          }).catch((err) => {
            reject(err);
          });
      } catch (ex) {
        reject(ex);
      }
    });
  }

  formatInvoices(invoices: Array<any>): Array<any> {
    try {
      let formatted = new Array<any>();
      let count = 0;

      for (let current = 0; current < invoices.length; current++) {

        if(invoices[current].status !== "draft"){
          let line = invoices[current].lines.data[0];

          let item = {
            id: invoices[current].id,
            date: moment.unix(invoices[current].date).format('YYYY-MM-DD'),
            amount: '$' + (invoices[current].amount_due / 100).toFixed(2),
            //description: line.plan.statement_descriptor
            description: line.plan ? line.plan.statement_descriptor : ''
          };
        formatted[count] = item;
        count++;
        }
      }
      return formatted;
    } catch (err) {
      return null;
    }
  }

  clearCustomer() {
    this.app.setCustomer(null);
  }

  getCustomer(): Promise<Customer> {
    return this.api.getApiClient().then(client => this.callGetCustomer(client));
  }

  private callGetCustomer(apigClient: any): Promise<Customer> {
    this.logger.log('billing.service', 'callGetCustomer', 'before promise');
    return new Promise<Customer>((resolve, reject) => {
      let params = {};
      let body = {};
      let additionalParams = {
        headers: { 'Content-Type': 'application/json' },
        queryParams: {}
      };
      try {
        apigClient.billingCustomerGet(params, body, additionalParams)
          .then((result) => {
            this.logger.log('billing.service', 'callGetCustomer result', result);
            let customer = new Customer(result.data);
            this.app.setCustomer(customer);

            resolve(customer);
          }).catch((err) => {
            reject(err);
          });
      } catch (ex) {
        reject(ex);
      }
    });
  }
}



// WEBPACK FOOTER //
// ./node_modules/angular-router-loader/src!./node_modules/angular2-template-loader!./node_modules/@angularclass/hmr-loader!./node_modules/tslint-loader!./src/app/shared/billing.service.ts